import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  OutlinedInput,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getUiSettings, postUiSettings } from "./uiSettingsActions";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import AlertDialog from "../../components/AlertDialog/AlertDialog";
import { RootState } from "../../store/store";
import { MuiColorInput } from "mui-color-input";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import {
  IPostUiSettingsRequestBody,
  IUiSettings,
} from "../../models/admin/uiSettings/uiSettings";
import CustomDialog from "../../common/CustomDialog/CustomDialog";
import CustomerLoginPreview from "./CustomerLoginPreview";
import CurrentUIConfiguration from "./CurrentUIConfiguration";

interface IProps {}

const UiSettings: FC<IProps> = () => {
  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const CommonFormFieldBox = styled(Box)({
    display: "grid",
    gridTemplateColumns: "0.5fr 1.5fr",
    alignItems: "center",
  });

  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width:768px) and (max-width:1200px)");

  const dispatch = useAppDispatch();

  const { uiSettings } = useAppSelector((state: RootState) => state.uiSettings);
  const { isSuccess, isError, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );

  const [formData, setFormData] = useState<IUiSettings>({});

  const [originalData, setOriginalData] = useState<IUiSettings>({});
  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false);
  const [loginBannerType, setLoginBannerType] =
    useState<string>("image_login_banner");

  const loginBannerOptions = [
    {
      featureCode: "image_login_banner",
      featureLabel: "Image",
    },
    {
      featureCode: "video_login_banner",
      featureLabel: "Video",
    },
  ];
  const handleLoginBannerType = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value;
      setLoginBannerType(value);
      setFormData((prevState) => ({
        ...prevState,
        loginBannerType: value === "video_login_banner" ? "video" : "image",
        // // Clear image when switching to video
        // loginBannerImage:
        //   value === "video_login_banner" ? null : prevState.loginBannerImage,
        // // Clear video URL when switching to image
        // loginBannerVideoUrl:
        //   value === "image_login_banner" ? null : prevState.loginBannerVideoUrl,
      }));
    },
    []
  );

  const getLoginBannerField = useCallback(() => {
    switch (loginBannerType) {
      case "image_login_banner":
        return (
          <Button
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
            sx={{ borderRadius: "4px" }}
          >
            Upload file
            <VisuallyHiddenInput
              type="file"
              onChange={(event) => handleFileChange(event, "loginBannerImage")}
            />
          </Button>
        );
      case "video_login_banner":
        return (
          <FormControl fullWidth>
            <InputLabel>Video Url</InputLabel>
            <OutlinedInput
              type={"text"}
              label={"Video Url"}
              value={formData?.loginBannerVideoUrl || null}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setFormData((prevState) => ({
                  ...prevState,
                  loginBannerVideoUrl: event.target.value,
                }));
              }}
              placeholder={"Enter Video Url"}
            />
          </FormControl>
        );
    }
  }, [loginBannerType, formData?.loginBannerVideoUrl]);

  const handleFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    fieldName: string
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();

      // Check for unsupported file types
      if (!file.type.startsWith("image/")) {
        alert("Please upload a valid image file. Only images are allowed.");
        return; // Stop processing if it's not an image
      }

      reader.onload = (e) => {
        img.src = e.target?.result as string;
        img.onload = () => {
          // Specific dimension check for loginBannerImage
          if (fieldName === "loginBannerImage") {
            const isValidDimensions = img.width === 788 && img.height === 644;
            if (!isValidDimensions) {
              alert("The login banner must be 788 x 644 pixels.");
              return;
            }
          }
          // If valid, update formData with the file
          setFormData((prevState) => ({ ...prevState, [fieldName]: file }));
        };
      };

      reader.readAsDataURL(file);
    }
  };
  const handleFileDelete = useCallback(
    (key: keyof IUiSettings) => {
      setFormData((prevState) => ({ ...prevState, [key]: originalData[key] }));
    },
    [originalData]
  );

  const handleCancelClick = useCallback(() => {
    setFormData(originalData);
  }, [originalData]);

  const handleSave = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAlertDialogOpen(true);
  }, []);

  const handleAlertDialogCancelClick = useCallback(() => {
    setAlertDialogOpen(false);
  }, []);

  const handleAlertDialogOkClick = useCallback(() => {
    const colorInputFields = {
      iconColor: {
        label: "Icon Color",
        data: formData.iconColor,
      },
      lightPrimaryMainColor: {
        label: "Primary Button Color",
        data: formData.lightPrimaryMainColor,
      },
      lightSecondaryMainColor: {
        label: "Secondary Button Color",
        data: formData.lightSecondaryMainColor,
      },
      lightPrimaryContrastTextColor: {
        label: "Primary Button Text Color",
        data: formData.lightPrimaryContrastTextColor,
      },
      lightSecondaryContrastTextColor: {
        label: "Secondary Button Text Color",
        data: formData.lightSecondaryContrastTextColor,
      },
    };

    for (const [field, { label, data }] of Object.entries(colorInputFields)) {
      if (!isValidHexCode(data || "")) {
        alert(`Please enter a valid hex color code for ${label}.`);
        return;
      }
    }
    setAlertDialogOpen(false);
    const newFormData: IPostUiSettingsRequestBody = new FormData();

    const appendFormData = (
      formData: FormData,
      data: any,
      parentKey: string | null = null
    ) => {
      Object.keys(data).forEach((key) => {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        const value = data[key];

        if (value instanceof File) {
          formData.append(fullKey, value);
        } else if (typeof value === "object" && value !== null) {
          formData.append(fullKey, JSON.stringify(value));
        } else if (typeof value === "string" || value !== null) {
          formData.append(fullKey, value);
        } else if (value === null) {
          formData.append(fullKey, ""); // Explicitly append null values as empty strings
        }
      });
    };

    if (formData) {
      const cleanFormData = {
        ...formData,
        loginBannerImage:
          formData.loginBannerType === "image"
            ? formData.loginBannerImage
            : null,
        loginBannerVideoUrl:
          formData.loginBannerType === "video"
            ? formData.loginBannerVideoUrl
            : null,
      };
      appendFormData(newFormData, cleanFormData);
      dispatch(postUiSettings(newFormData));
    }
  }, [dispatch, formData]);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(getUiSettings());
  }, [dispatch]);

  const handleDrop = (event: React.DragEvent<HTMLInputElement>) => {
    event.preventDefault();

    const data = event.dataTransfer;
    if (data && data.items) {
      for (let i = 0; i < data.items.length; i++) {
        const item = data.items[i];
        if (item.kind === "string") {
          item.getAsString((url) => {
            if (
              url &&
              (url.startsWith("http://") || url.startsWith("https://"))
            ) {
              // Prevent handling URLs, only colors should be allowed
              return;
            }
          });
        }
      }
    }
  };

  const isValidHexCode = (value: string) => {
    const hexRegex = /^#([0-9A-Fa-f]{3}){1,2}$/;
    return hexRegex.test(value);
  };

  useEffect(() => {
    if (uiSettings) {
      setFormData(uiSettings);
      setOriginalData(uiSettings);
      setLoginBannerType(
        uiSettings.loginBannerType === "video"
          ? "video_login_banner"
          : "image_login_banner"
      );
    }
  }, [uiSettings]);

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "uiSettings/postUiSettings"
    ) {
      setMessageDialogOpen(true);
      if (isSuccess) {
        setOriginalData(formData);
      }
    }
  }, [isSuccess, isError, calledReducerType, formData]);
  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: isMobile ? "flex" : "grid",
          flexDirection: isMobile ? "column" : "unset",
          gridTemplateColumns: isTablet ? "1fr 1fr" : "2fr 1fr",
          gap: "20px",
          paddingY: 4,
        }}
        id="ui_settings_section"
        data-test-id="ui_settings_section"
        aria-label="UI Settings Section"
      >
        <form
          onSubmit={handleSave}
          style={{ display: "flex", flexDirection: "column", gap: "2px" }}
          id="ui_settings_form"
          data-test-id="ui_settings_form"
          aria-label="UI Settings Form"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
              height: "100%",
            }}
            id="ui_settings_form_content"
            data-test-id="ui_settings_form_content"
            aria-label="UI Settings Form Content"
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.5fr",
                alignItems: "center",
              }}
              id="ui_settings_logo_section"
              data-test-id="ui_settings_logo_section"
              aria-label="Logo Settings Section"
            >
              <Typography id="ui_settings_logo_section_title" data-test-id="ui_settings_logo_section_title" aria-label="Logo Section Title">Logo</Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                id="ui_settings_logo_actions"
                data-test-id="ui_settings_logo_actions"
                aria-label="Logo Actions"
              >
                <Button
                  component="label"
                  role={undefined}
                  variant="contained"
                  tabIndex={-1}
                  startIcon={<CloudUploadIcon />}
                  sx={{ borderRadius: "4px" }}
                  id="ui_settings_logo_upload"
                  data-test-id="ui_settings_logo_upload"
                  aria-label="Upload Logo"
                >
                  Upload file
                  <VisuallyHiddenInput
                    type="file"
                    onChange={(event) => handleFileChange(event, "logo")}
                    id="ui_settings_logo_input"
                    data-test-id="ui_settings_logo_input"
                    aria-label="Logo File Input"
                  />
                </Button>
                <Box>
                  {formData && formData.logo instanceof File ? (
                    <Chip
                      label={formData.logo.name}
                      onDelete={() => handleFileDelete("logo")}
                      id="ui_settings_logo_chip"
                      data-test-id="ui_settings_logo_chip"
                      aria-label="Selected Logo File"
                    />
                  ) : (
                    <></>
                  )}
                </Box>
              </Box>
            </Box>
            <CommonFormFieldBox
              id="ui_settings_banner_section"
              data-test-id="ui_settings_banner_section"
              aria-label="Login Banner Settings Section"
            >
              <Typography sx={{ marginTop: "5px" }}>Login Banner</Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "start",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
                id="ui_settings_banner_content"
                data-test-id="ui_settings_banner_content"
                aria-label="Login Banner Content"
              >
                <FormControl>
                  <RadioGroup
                    value={loginBannerType}
                    sx={{ display: "flex", flexDirection: "row" }}
                    onChange={handleLoginBannerType}
                    id="ui_settings_banner_type"
                    data-test-id="ui_settings_banner_type"
                    aria-label="Login Banner Type"
                  >
                    {loginBannerOptions.map((option) => (
                      <FormControlLabel
                        key={option.featureCode}
                        value={option.featureCode}
                        control={<Radio />}
                        label={option.featureLabel}
                        id={`ui_settings_banner_option_${option.featureCode}`}
                        data-test-id={`ui_settings_banner_option_${option.featureCode}`}
                        aria-label={`${option.featureLabel} Banner Type`}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                {getLoginBannerField()}
                <Box>
                  {formData &&
                  formData.loginBannerImage !== null &&
                  formData.loginBannerImage instanceof File ? (
                    <Chip
                      label={formData.loginBannerImage.name}
                      onDelete={() => handleFileDelete("loginBannerImage")}
                      id="ui_settings_banner_chip"
                      data-test-id="ui_settings_banner_chip"
                      aria-label="Selected Banner File"
                    />
                  ) : (
                    <></>
                  )}
                </Box>
              </Box>
            </CommonFormFieldBox>
            <CommonFormFieldBox
              id="ui_settings_loader_section"
              data-test-id="ui_settings_loader_section"
              aria-label="Loader Settings Section"
            >
              <Typography id="ui_settings_loader_section_title" data-test-id="ui_settings_loader_section_title" aria-label="Loader Section Title">Loader</Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                id="ui_settings_loader_actions"
                data-test-id="ui_settings_loader_actions"
                aria-label="Loader Actions"
              >
                <Button
                  component="label"
                  role={undefined}
                  variant="contained"
                  tabIndex={-1}
                  startIcon={<CloudUploadIcon />}
                  sx={{ borderRadius: "4px" }}
                  id="ui_settings_loader_upload"
                  data-test-id="ui_settings_loader_upload"
                  aria-label="Upload Loader"
                >
                  Upload file
                  <VisuallyHiddenInput
                    type="file"
                    onChange={(event) => handleFileChange(event, "loader")}
                    id="ui_settings_loader_input"
                    data-test-id="ui_settings_loader_input"
                    aria-label="Loader File Input"
                  />
                </Button>
                <Box>
                  {formData && formData.loader instanceof File ? (
                    <Chip
                      label={formData.loader.name}
                      onDelete={() => handleFileDelete("loader")}
                      id="ui_settings_loader_chip"
                      data-test-id="ui_settings_loader_chip"
                      aria-label="Selected Loader File"
                    />
                  ) : (
                    <></>
                  )}
                </Box>
              </Box>
            </CommonFormFieldBox>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.5fr",
                alignItems: "center",
              }}
              id="ui_settings_icon_color_section"
              data-test-id="ui_settings_icon_color_section"
              aria-label="Icon Color Settings Section"
            >
              <Typography id="ui_settings_icon_color_section_title" data-test-id="ui_settings_icon_color_section_title" aria-label="Icon Color Section Title">Icon Color</Typography>
              <MuiColorInput
                size={isMobile ? "small" : "medium"}
                format="hex"
                isAlphaHidden
                value={formData && formData.iconColor ? formData.iconColor : ""}
                onChange={(newValue) =>
                  setFormData((prevState) => ({
                    ...prevState,
                    iconColor: newValue,
                  }))
                }
                onDrop={handleDrop}
                id="ui_settings_icon_color_input"
                data-test-id="ui_settings_icon_color_input"
                aria-label="Icon Color Input"
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.5fr",
                alignItems: "center",
              }}
              id="ui_settings_primary_button_color_section"
              data-test-id="ui_settings_primary_button_color_section"
              aria-label="Primary Button Color Settings Section"
            >
              <Typography id="ui_settings_primary_button_color_section_title" data-test-id="ui_settings_primary_button_color_section_title" aria-label="Primary Button Color Section Title">Primary Button Color</Typography>
              <MuiColorInput
                size={isMobile ? "small" : "medium"}
                format="hex"
                isAlphaHidden
                value={formData?.lightPrimaryMainColor || ""}
                onChange={(newValue) =>
                  setFormData((prevState) => ({
                    ...prevState,
                    lightPrimaryMainColor: newValue,
                  }))
                }
                onDrop={handleDrop}
                id="ui_settings_primary_button_color_input"
                data-test-id="ui_settings_primary_button_color_input"
                aria-label="Primary Button Color Input"
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.5fr",
                alignItems: "center",
              }}
              id="ui_settings_secondary_button_color_section"
              data-test-id="ui_settings_secondary_button_color_section"
              aria-label="Secondary Button Color Settings Section"
            >
              <Typography id="ui_settings_secondary_button_color_section_title" data-test-id="ui_settings_secondary_button_color_section_title" aria-label="Secondary Button Color Section Title">Secondary Button Color</Typography>
              <MuiColorInput
                size={isMobile ? "small" : "medium"}
                format="hex"
                isAlphaHidden
                value={formData.lightSecondaryMainColor || ""}
                onChange={(newValue) =>
                  setFormData((prevState) => ({
                    ...prevState,
                    lightSecondaryMainColor: newValue,
                  }))
                }
                onDrop={handleDrop}
                id="ui_settings_secondary_button_color_input"
                data-test-id="ui_settings_secondary_button_color_input"
                aria-label="Secondary Button Color Input"
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.5fr",
                alignItems: "center",
              }}
              id="ui_settings_primary_button_text_color_section"
              data-test-id="ui_settings_primary_button_text_color_section"
              aria-label="Primary Button Text Color Settings Section"
            >
              <Typography id="ui_settings_primary_button_text_color_section_title" data-test-id="ui_settings_primary_button_text_color_section_title" aria-label="Primary Button Text Color Section Title">Primary Button Text Color</Typography>
              <MuiColorInput
                size={isMobile ? "small" : "medium"}
                format="hex"
                isAlphaHidden
                value={formData.lightPrimaryContrastTextColor || ""}
                onChange={(newValue) =>
                  setFormData((prevState) => ({
                    ...prevState,
                    lightPrimaryContrastTextColor: newValue,
                  }))
                }
                onDrop={handleDrop}
                id="ui_settings_primary_button_text_color_input"
                data-test-id="ui_settings_primary_button_text_color_input"
                aria-label="Primary Button Text Color Input"
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.5fr",
                alignItems: "center",
              }}
              id="ui_settings_secondary_button_text_color_section"
              data-test-id="ui_settings_secondary_button_text_color_section"
              aria-label="Secondary Button Text Color Settings Section"
            >
              <Typography id="ui_settings_secondary_button_text_color_section_title" data-test-id="ui_settings_secondary_button_text_color_section_title" aria-label="Secondary Button Text Color Section Title">Secondary Button Text Color</Typography>
              <MuiColorInput
                size={isMobile ? "small" : "medium"}
                format="hex"
                isAlphaHidden
                value={formData.lightSecondaryContrastTextColor || ""}
                onChange={(newValue) =>
                  setFormData((prevState) => ({
                    ...prevState,
                    lightSecondaryContrastTextColor: newValue,
                  }))
                }
                onDrop={handleDrop}
                id="ui_settings_secondary_button_text_color_input"
                data-test-id="ui_settings_secondary_button_text_color_input"
                aria-label="Secondary Button Text Color Input"
              />
            </Box>
            <Box 
              sx={{ display: "flex", gap: "20px", marginTop: "40px" }}
              id="ui_settings_actions"
              data-test-id="ui_settings_actions"
              aria-label="UI Settings Actions"
            >
              <Button
                variant="contained"
                sx={{
                  width: "max-content",
                  marginTop: "20px",
                  borderRadius: "4px",
                }}
                onClick={() => setPreviewDialogOpen(true)}
                disabled={
                  JSON.stringify(formData) === JSON.stringify(originalData)
                }
                id="ui_settings_preview_button"
                data-test-id="ui_settings_preview_button"
                aria-label="Preview UI Settings"
              >
                Preview
              </Button>
              <Button
                variant="contained"
                sx={{
                  width: "max-content",
                  marginTop: "20px",
                  borderRadius: "4px",
                }}
                onClick={handleCancelClick}
                id="ui_settings_cancel_button"
                data-test-id="ui_settings_cancel_button"
                aria-label="Cancel UI Settings Changes"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  width: "max-content",
                  marginTop: "20px",
                  borderRadius: "4px",
                }}
                disabled={
                  JSON.stringify(formData) === JSON.stringify(originalData)
                }
                id="ui_settings_save_button"
                data-test-id="ui_settings_save_button"
                aria-label="Save UI Settings"
              >
                Save
              </Button>
            </Box>
          </Box>
        </form>
        <CurrentUIConfiguration
          key={JSON.stringify(uiSettings)}
          formData={formData}
        />
      </Box>
      <AlertDialog
        handleCancelClick={handleAlertDialogCancelClick}
        handleOkClick={handleAlertDialogOkClick}
        open={alertDialogOpen}
        title="Saving this configuration will impact the end user, is this okay?"
      />
      <MessageDialog
        handleClose={handleMessageDialogClose}
        open={messageDialogOpen}
      />
      <CustomDialog
        maxWidth="xl"
        onClose={() => setPreviewDialogOpen(false)}
        open={previewDialogOpen}
        showDivider={false}
        content={
          <CustomerLoginPreview
            isFunctionable={false}
            loginBannerSrc={
              formData.loginBannerType === "image"
                ? formData.loginBannerImage
                : formData.loginBannerVideoUrl || ""
            }
            loginBannerType={formData.loginBannerType}
            logoSrc={formData.logo || ""}
          />
        }
        title="Preview"
      />
    </>
  );
};

export default UiSettings;
